import { CSSResultGroup, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js'; // Import decorators from 'lit/decorators.js' in v3
import { classMap } from 'lit/directives/class-map.js'; // Import classMap directive

import { spinnerGradient } from '../../icons/spinner-gradient';
import { spinnerWhite } from '../../icons/spinner-white';
import { buttonStyles } from './button.styles';

export type ButtonSize = 'large' | 'small';
export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'launch'
  | 'link'
  | 'danger'
  | 'icon';

/**
 * Button used for alerts and notification
 *
 * @prop {boolean} processing - If the button is processing.
 * @prop {boolean} disabled - If the button is disabled.
 * @prop {ButtonSize} size - Sets the size of button.
 * @prop {ButtonVariant} variant - Sets the type of button like Primary, Secondary etc...
 *
 * @fires click - Fires when the button is clicked.
 */
@customElement('cds-button')
export class ButtonElement extends LitElement {
  static styles: CSSResultGroup = [buttonStyles];

  @property({ type: Boolean, reflect: true }) processing = false;
  @property({ type: Boolean, reflect: true }) disabled = false;
  @property({ type: String }) size: ButtonSize = 'large';
  @property({ type: String }) type: 'button' | 'submit' = 'button';
  /** @prop {ButtonVariant} variant - Sets the type of button like Primary, Secondary etc... */
  @property({ type: String }) variant: ButtonVariant = 'secondary';

  render() {
    // class map for dynamic class binding
    const classes = {
      [`${this.variant}`]: true,
      [`${this.size}`]: true,
      'processing': this.processing && !this.disabled,
      disabled: this.disabled || this.processing,
    };



    if (this.variant === 'icon') {
      // Prevent size class from being applied to the icon button
      // Size will be controlled by the cds-icon provided at the app-level
      delete classes[`${this.size}`];
    }

    return html`
      <button
        class="${classMap(classes)}"
        ?disabled=${this.disabled || this.processing}
        type="${this.type}"
        @click=${this._handleClick}
      >
        <slot></slot>
      </button>
      ${this.processing ? html`
        <svg class="processing-icon" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          ${this.variant === 'secondary' || this.variant === 'link' || this.variant === 'icon' ? spinnerGradient : spinnerWhite}
        </svg>
      ` : ''}
    `;
  }

  private _handleClick(e: MouseEvent) {
    if (this.type === 'submit') {
      const form = this.closest('form');
      if (form) {
        e.preventDefault();
        form.requestSubmit();
      }
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cds-button': ButtonElement;
  }
}
